<template>
  <div class="analytics-dashboard" v-if="analyticsData">

    <strategy-table v-if="showUserStrategies" @close="showUserStrategies = false" />
    <battery-history-table v-if="showBatteryHistory"  @close="showBatteryHistory = false" />

    <div class="analytics-controls">
      <button class="btn btn-accent back-to-navigator" @click="$emit('showNavigator')">
        Вернуться к Навигатору
      </button>

      <div class="battery-wrapper">
        <BatteryIndicator @click="showBatteryHistory = true" :level="batteryPercent" />
      </div>
    </div>
    <div class="top-cards">
      <div class="metric-card">
        <div class="metric-title">Успешность выполнения задачи</div>

        <div class="arc-gauge-wrap">
          <svg class="arc-gauge" viewBox="0 0 220 150">
            <path
                class="arc-gauge-track"
                d="M 49.4 130 A 70 70 0 1 1 170.6 130"
                pathLength="100"
            />
            <path
                class="arc-gauge-progress"
                d="M 49.4 130 A 70 70 0 1 1 170.6 130"
                pathLength="100"
                :style="buildArcGaugeStyle(analyticsData.task_success.value, analyticsData.task_success.color)"
            />
          </svg>

          <div class="arc-gauge-content">
            <div class="metric-value">{{ formatMetricValue(analyticsData.task_success.value) }}</div>
            <div class="stars">
        <span
            v-for="i in 5"
            :key="i"
            :class="{ active: i <= Math.round(analyticsData.task_success.value) }"
        >
          ★
        </span>
            </div>
          </div>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-title">Эффективность эмоций</div>

        <div class="arc-gauge-wrap">
          <svg class="arc-gauge" viewBox="0 0 220 150">
            <path
                class="arc-gauge-track"
                d="M 49.4 130 A 70 70 0 1 1 170.6 130"
                pathLength="100"
            />
            <path
                class="arc-gauge-progress"
                d="M 49.4 130 A 70 70 0 1 1 170.6 130"
                pathLength="100"
                :style="buildArcGaugeStyle(analyticsData.emotion_effectiveness.value, analyticsData.emotion_effectiveness.color)"
            />
          </svg>

          <div class="arc-gauge-content">
            <div class="metric-value">{{ formatMetricValue(analyticsData.emotion_effectiveness.value) }}</div>
            <div class="stars">
        <span
            v-for="i in 5"
            :key="i"
            :class="{ active: i <= Math.round(analyticsData.emotion_effectiveness.value) }"
        >
          ★
        </span>
            </div>
          </div>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-title">Процент “совпадений”</div>

        <div class="percent-gauge-wrap">
          <svg class="percent-gauge" viewBox="0 0 180 180">
            <circle
                class="percent-gauge-track"
                cx="90"
                cy="90"
                r="62"
            />
            <circle
                class="percent-gauge-progress"
                cx="90"
                cy="90"
                r="62"
                :style="buildPercentGaugeStyle(analyticsData.match_percent.value, analyticsData.match_percent.color)"
            />
          </svg>

          <div class="percent-gauge-content">
            <div class="metric-value">{{ analyticsData.match_percent.value }}%</div>
          </div>
        </div>
      </div>
    </div>

    <div class="middle-cards">
      <div class="distribution-card">
        <div class="section-title">Распределение эмоциональных состояний по квадратам</div>

        <div class="distribution-content">
          <div class="distribution-chart-block">
            <div class="distribution-chart-wrap">
              <div
                  class="distribution-chart"
                  :style="distributionChartStyle"
              >
                <div class="distribution-chart-center">
                  <div class="distribution-center-label">ВСЕГО СЕССИЙ</div>
                  <div class="distribution-center-value">{{ analyticsData.sessions_total }}</div>
                </div>
              </div>

              <template v-if="distributionSegments.length">
                <div
                    v-for="segment in distributionSegments"
                    :key="segment.key"
                    class="distribution-label"
                    :style="getDistributionLabelStyle(segment)"
                >
                  {{ segment.percent }}%
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="mismatch-card">
        <div class="mismatch-header">
          <div class="section-title">Несовпадение эмоций и задачи</div>
          <div class="pill">{{ analyticsData.mismatch.total }}</div>
        </div>

        <div class="mismatch-row">
          <div class="mismatch-label">
            <img src="@/assets/images/mood_tracker/target-board.svg" height="35" alt="Иконка цели" />
            <div>
              Изменение задачи
            </div>
          </div>
          <div class="progress-line">
            <div
                class="progress-line-fill"
                :class="{'progress-line-empty': !analyticsData.mismatch.task_change}"
                :style="{ width: `${analyticsData.mismatch.task_change}%` }"
            >
              {{ analyticsData.mismatch.task_change }}%
            </div>
          </div>
        </div>

        <div class="mismatch-row">
          <div class="mismatch-label">
            <img src="@/assets/images/mood_tracker/speed.svg" height="35" alt="Иконка спидометра" />
            <div>Изменение эмоционального состояния</div>
          </div>
          <div class="progress-line">
            <div
                class="progress-line-fill"
                :class="{'progress-line-empty': !analyticsData.mismatch.emotion_change}"
                :style="{ width: `${analyticsData.mismatch.emotion_change}%` }"
            >
              {{ analyticsData.mismatch.emotion_change }}%
            </div>
          </div>
        </div>

        <button class="switchers-btn" @click="showUserStrategies = true">
          <div>
            <img src="@/assets/images/mood_tracker/case.svg" height="40" alt="иконка портфеля" />
          </div>
          <span>Мои личные переключатели</span>
        </button>
      </div>
    </div>

    <div class="bottom-cards">
      <div class="info-card">
        <div class="section-title">Самая частый квадрат ИН недели</div>
        <div class="info-card-wrapper" v-if="analyticsData.week_quadrant?.color">
          <div class="info-name" :class="analyticsData.week_quadrant.color ?? ''">{{ analyticsData.week_quadrant.title }}</div>
          <div class="info-percent">{{ analyticsData.week_quadrant.percent }}%</div>
        </div>
        <div v-else>
          Нет данных
        </div>
      </div>

      <div class="info-card">
        <div class="section-title">Самая частая задача недели</div>
        <div class="info-card-wrapper" v-if="analyticsData.week_task?.color">
          <div class="info-name" :class="analyticsData.week_task.color ?? ''">{{ analyticsData.week_task.title }}</div>
          <div class="info-percent">{{ analyticsData.week_task.percent }}%</div>
        </div>
        <div v-else>
          Нет данных
        </div>
      </div>

      <div class="info-card">
        <div class="section-title">Самая полезная стратегия недели</div>
        <div class="strategy-text" v-if="analyticsData.week_strategy?.text">
          <strong>{{ analyticsData.week_strategy.title }}</strong>
          <p>{{ analyticsData.week_strategy.text }}</p>
        </div>
        <div v-else>
          Нет данных
        </div>
      </div>
    </div>
  </div>
  <div v-else class="loader">
    <preloader />
  </div>
</template>

<script>
import {computed, onMounted, ref} from 'vue'
import {onBeforeMount, watch} from "@vue/runtime-core";
import {useStore} from "vuex";
import Preloader from "@/components/Technical/Preloader.vue";
import StrategyTable from "@/views/MoodTracker/MoodTrackerUserStrategies.vue";
import BatteryHistoryTable from "@/views/MoodTracker/BatteryAnalytics.vue";
import BatteryIndicator from "@/views/MoodTracker/BatteryIndicator.vue";

export default {
  name: 'MoodTrackerAnalyticsDashboard',
  components: {BatteryIndicator, BatteryHistoryTable, StrategyTable, Preloader},
  setup() {
    const showUserStrategies = ref(false);
    const showBatteryHistory = ref(false);
    const batteryPercent = computed(() => store.state.tracker.batteryLevel);

    const quadrantColorMap = {
      red: '#fd0a07',
      yellow: '#fde80c',
      green: '#6dba33',
      blue: '#183695',
    }

    const distributionSegments = computed(() => {
      const distribution = analyticsData.value.quadrant_distribution || {}
      const orderedKeys = ['red', 'yellow', 'green', 'blue']

      const rawSegments = orderedKeys
          .map(key => ({
            key,
            percent: Number(distribution[key] || 0),
            color: quadrantColorMap[key] || '#D9D9D9',
          }))
          .filter(segment => segment.percent > 0)

      if (!rawSegments.length) {
        return []
      }

      let currentAngle = 0

      return rawSegments.map(segment => {
        const angle = (segment.percent / 100) * 360
        const startAngle = currentAngle
        const endAngle = currentAngle + angle
        const midAngle = startAngle + angle / 2
        currentAngle = endAngle

        return {
          ...segment,
          startAngle,
          endAngle,
          midAngle,
        }
      })
    });

    const distributionChartStyle = computed(() => {
      if (!distributionSegments.value.length) {
        return {
          background: 'conic-gradient(#D9D9D9 0deg 360deg)',
        }
      }

      const parts = distributionSegments.value.map(segment =>
          `${segment.color} ${segment.startAngle}deg ${segment.endAngle}deg`
      )

      return {
        background: `conic-gradient(${parts.join(', ')})`,
      }
    })

    const getDistributionLabelStyle = segment => {
      const centerX = 130
      const centerY = 130

      const radius = 88
      const angleInRadians = ((segment.midAngle - 90) * Math.PI) / 180

      const x = centerX + Math.cos(angleInRadians) * radius
      const y = centerY + Math.sin(angleInRadians) * radius

      return {
        left: `${x}px`,
        top: `${y}px`,
        transform: 'translate(-50%, -50%)',
        color: '#fff',
      }
    }

    const store = useStore();
    const isLoading = ref(false);
    const userAnalytics = computed(() => store.state.tracker.analytics);
    const analyticsData = ref(null);

    onBeforeMount(() => {
      isLoading.value = true;

      store.dispatch('tracker/getAnalytics').then(() => {
        prepareAnalyticsData();
        isLoading.value = false;
      });
    });

    const colorsMap = {
      red: 'Красный',
      yellow: 'Жёлтый',
      blue: 'Синий',
      green: 'Зелёный',
    };

    const prepareAnalyticsData = () => {
      let preparedColorsData = {
        red: 0,
        blue: 0,
        yellow: 0,
        green: 0,
      };

      userAnalytics.value.summary.colors_distribution_percent.forEach((el) => {
        let colorKey = getColorKeyByName(el.color_name);
        preparedColorsData[colorKey] = el.percent;
      });

      analyticsData.value = {
        task_success: {
          value: userAnalytics.value.feedback?.average_successful_rate ?? 0,
          color: '#6C5CE7',
        },
        emotion_effectiveness: {
          value: userAnalytics.value.feedback?.average_effective_rate ?? 0,
          color: '#44C0E6',
        },
        match_percent: {
          value: userAnalytics.value.changes?.unchanged_tasks_percent ?? 0,
          color: '#7B3FB4',
        },
        sessions_total: userAnalytics.value.summary?.overall_occurred_tasks_count ?? 0,
        quadrant_distribution: preparedColorsData,
        mismatch: {
          total:  userAnalytics.value.changes?.changed_tasks_count ?? 0,
          task_change:  userAnalytics.value.changes?.changed_task_categories_percent ?? 0,
          emotion_change:  userAnalytics.value.changes?.changed_colors_percent ?? 0,
        },
        week_quadrant: {
          title:  userAnalytics.value.weekly.most_used_color ? (userAnalytics.value.weekly.most_used_color.color_name + ' КВАДРАТ') : null,
          percent: userAnalytics.value.weekly.most_used_color?.percent ?? 0,
          color: userAnalytics.value.weekly.most_used_color ? getColorKeyByName(userAnalytics.value.weekly.most_used_color.color_name) : null,
        },
        week_task: {
          title: userAnalytics.value.weekly.most_used_task?.task_name ?? null,
          percent: userAnalytics.value.weekly.most_used_task?.percent ?? 0,
          color: userAnalytics.value.weekly.most_used_task ? getColorKeyByName(userAnalytics.value.weekly.most_used_task.color_name) : null,
        },
        week_strategy: {
          text: userAnalytics.value.weekly?.most_helpful_strategy ? formatString(userAnalytics.value.weekly.most_helpful_strategy.text.slice(0, 25)) : null,
        },
      }
    }

    const getColorKeyByName = (colorName) => {
      return Object.keys(colorsMap).find(key => colorsMap[key] === colorName);
    }

    const formatString = (string, number) => {
      let finString = string;
      if (string.length > number) {
         finString = string.substring(0, (number - 3)) + "...";
      }

      return finString;
    }

    const formatMetricValue = value => {
      return Number(value || 0).toFixed(1).replace('.', ',')
    }

    const buildArcGaugeStyle = (value, color) => {
      const safeValue = Math.max(0, Math.min(Number(value) || 0, 5))
      const progress = (safeValue / 5) * 100

      return {
        stroke: progress ? color : null,
        strokeDasharray: `${progress} 100`,
      }
    }

    const PERCENT_RADIUS = 62
    const PERCENT_CIRCUMFERENCE = 2 * Math.PI * PERCENT_RADIUS

    const buildPercentGaugeStyle = (value, color) => {
      const safeValue = Math.max(0, Math.min(Number(value) || 0, 100))
      const progress = safeValue / 100
      const dashOffset = PERCENT_CIRCUMFERENCE * (1 - progress)

      return {
        stroke: color,
        strokeDasharray: PERCENT_CIRCUMFERENCE,
        strokeDashoffset: dashOffset,
      }
    }

    const trackerSettings = computed(() => store.state.tracker.settingsData);

    watch(() => {
      trackerSettings.value?.current_mode
    }, () => {
      store.dispatch('tracker/getAnalytics').then(() => {
        prepareAnalyticsData();
      });
    }, {deep: true});

    return {
      analyticsData,
      distributionSegments,
      distributionChartStyle,
      getDistributionLabelStyle,
      formatMetricValue,
      buildArcGaugeStyle,
      buildPercentGaugeStyle,
      showUserStrategies,
      showBatteryHistory,
      batteryPercent
    }
  },
}
</script>

<style scoped>
.analytics-dashboard {
  border-radius: 42px;
  padding: 20px 0;
  width: 100%;
  box-sizing: border-box;
  font-family: Arial, sans-serif;
}

.dashboard-title {
  margin: 0 0 18px;
  font-size: 36px;
  font-weight: 800;
  color: #1f1f1f;
}

.top-cards,
.bottom-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.middle-cards {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 18px;
  margin: 18px 0;
}

.metric-card,
.distribution-card,
.mismatch-card,
.info-card {
  background: #f8f8f8;
  border-radius: 24px;
  padding: 18px;
  box-sizing: border-box;
}

.metric-card {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.section-title {
  margin-bottom: 8px;
}

.metric-title,
.section-title {
  font-size: 18px;
  line-height: 1.2;
}

.metric-title {
  text-align: center;
  margin-bottom: 14px;
}

.progress-ring {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  padding: 14px;
  transform: rotate(-135deg);
  margin-top: auto;
  margin-bottom: auto;
}

.percent-ring {
  width: 165px;
  height: 165px;
}

.progress-ring-inner {
  width: 100%;
  height: 100%;
  background: #f8f8f8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  transform: rotate(135deg);
}

.metric-value {
  font-size: 34px;
  font-weight: 800;
  color: #111;
}

.stars {
  margin-top: 6px;
  font-size: 18px;
  color: #d7d7d7;
}

.stars .active {
  color: inherit;
}

.metric-card:nth-child(1) .stars .active {
  color: #6c5ce7;
}

.metric-card:nth-child(2) .stars .active {
  color: #44c0e6;
}

.distribution-content {
  display: flex;
  gap: 18px;
  justify-content: center;
  margin-top: 16px;
}

.distribution-text {
  flex: 1;
  font-size: 16px;
  color: #444;
  line-height: 1.5;
}

.distribution-chart-block {
  display: flex;
  align-items: center;
  justify-content: center;
}

.distribution-chart {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  position: relative;
  flex-shrink: 0;
}

.distribution-chart-center {
  position: absolute;
  inset: 38px;
  background: #f8f8f8;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.distribution-center-label {
  font-size: 13px;
  color: #999;
  text-align: center;
}

.distribution-center-value {
  font-size: 34px;
  font-weight: 800;
  color: #222;
}

.distribution-label {
  position: absolute;
  font-size: 12px;
  font-weight: 700;
  color: white;
}

.mismatch-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pill {
  min-width: 58px;
  height: 38px;
  padding: 0 16px;
  border-radius: 999px;
  background: #ececec;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 800;
}

.mismatch-row {
  margin-top: 24px;
}

.mismatch-label {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
}

.progress-line {
  width: 100%;
  height: 34px;
  background: #dedede;
  border-radius: 999px;
  overflow: hidden;
}

.progress-line-fill {
  height: 100%;
  background: #5CC063;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 12px;
  box-sizing: border-box;
  font-size: 18px;
  font-weight: 800;
  color: #111;
  min-width: 62px;
}

.progress-line-empty {
  background: none;
}

.switchers-btn {
  margin-top: 28px;
  width: 100%;
  border: 3px solid #5CC063;
  background: #f8f8f8;
  border-radius: 999px;
  min-height: 74px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 22px;
  font-size: 18px;
  font-weight: 800;
  color: #222;
  cursor: pointer;
  justify-content: center;
}

.switchers-btn:hover {
  opacity: 0.8;
}

.switchers-icon {
  font-size: 28px;
}

.info-card {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.info-icon {
  font-size: 64px;
  margin: 20px 0 14px;
  color: #63aef5;
}

.info-name {
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 10px;
  text-transform: uppercase;
  word-break: break-word;
}

.info-name.yellow {
  color: var(--tracker-color-yellow);
}

.info-name.blue {
  color: var(--tracker-color-blue);
}

.info-name.green {
  color: var(--tracker-color-green);
}

.info-name.red {
  color: var(--tracker-color-red);
}

.info-card-wrapper {
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
}

.info-percent {
  font-size: 54px;
  font-weight: 900;
}

.strategy-text {
  margin-top: auto;
  margin-bottom: auto;
  font-size: 18px;
  line-height: 1.35;
}

.strategy-text strong {
  display: block;
  margin-bottom: 18px;
  font-size: 26px;
}

@media (max-width: 1250px) {
  .bottom-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1100px) {
  .top-cards,
  .middle-cards {
    grid-template-columns: 1fr;
  }

  .distribution-content {
    flex-direction: column;
    align-items: center;
  }
}

.distribution-chart-wrap {
  position: relative;
  width: 260px;
  height: 260px;
  flex-shrink: 0;
}

.distribution-chart {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  position: absolute;
  top: 20px;
  left: 20px;
}

.distribution-chart-center {
  position: absolute;
  inset: 38px;
  background: #f8f8f8;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.distribution-center-label {
  font-size: 13px;
  color: #999;
  text-align: center;
}

.distribution-center-value {
  font-size: 34px;
  font-weight: 800;
}

.arc-gauge-wrap {
  position: relative;
  width: 220px;
  height: 145px;
  margin-top: auto;
  margin-bottom: auto;
}

.arc-gauge {
  display: block;
  width: 220px;
  height: 145px;
  overflow: visible;

  .metric-value {
    font-size: 34px;
    font-weight: 800;
    line-height: 1;
  }
}

.arc-gauge-track,
.arc-gauge-progress {
  fill: none;
  stroke-width: 16;
}

.arc-gauge-track {
  stroke: #dedede;
  stroke-linecap: butt;
}

.arc-gauge-progress {
  stroke-linecap: round;
}

.arc-gauge-content {
  position: absolute;
  left: 50%;
  top: 64px;
  transform: translateX(-50%);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.metric-value {
  font-size: 34px;
  font-weight: 800;
  color: #111;
  line-height: 1;
}

.percent-gauge-wrap {
  position: relative;
  width: 180px;
  height: 180px;
  margin-top: auto;
  margin-bottom: auto;
}

.percent-gauge {
  width: 180px;
  height: 180px;
  display: block;
  overflow: visible;
  transform: scaleX(-1);
}

.percent-gauge-track,
.percent-gauge-progress {
  fill: none;
  stroke-width: 16;
  transform-origin: 90px 90px;
  transform: rotate(-90deg);
}

.percent-gauge-track {
  stroke: #E2E2E2;
}

.percent-gauge-progress {
  stroke-linecap: round;
  transition: stroke-dashoffset 0.4s ease;
}

.percent-gauge-content {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader {
  min-height: 1100px;
  margin: 80px 0;
  text-align: center;
}

.analytics-controls {
  margin-bottom: 16px;
}

.back-to-navigator {
   border-radius: 16px;
   font-weight: 600;
}

.battery-wrapper {
  margin-left: auto;
  cursor: pointer;
}

.battery-wrapper:hover {
  opacity: 0.9;
}

.analytics-controls {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
</style>